// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Global hover tooltip driven by [data-tip] attributes anywhere in the document. Canonical
// factory pattern, copied from patterns/tooltip.ts.
//
// It shows and hides purely by toggling a `visible` class, so the stylesheet MUST define both
// `.hover-tip` and `.hover-tip.visible`. With only the first rule the tooltip is painted on the
// first hover and never hidden again — it simply follows the pointer around the page, covering
// whatever is under it, while the class is toggled correctly the whole time. A source check in
// the test suite asserts both rules exist.

let tip: HTMLDivElement | null = null;

function ensure(): HTMLDivElement {
  if (!tip) {
    tip = document.createElement('div');
    tip.className = 'hover-tip';
    tip.setAttribute('role', 'tooltip');
    document.body.appendChild(tip);
  }
  return tip;
}

function position(el: HTMLDivElement, x: number, y: number): void {
  const pad = 12;
  const rect = el.getBoundingClientRect();
  let left = x + 14;
  let top = y + 14;
  if (left + rect.width + pad > window.innerWidth) left = x - rect.width - 14;
  if (top + rect.height + pad > window.innerHeight) top = y - rect.height - 14;
  el.style.left = `${Math.max(pad, left)}px`;
  el.style.top = `${Math.max(pad, top)}px`;
}

export function initTooltip(): void {
  let activeText = '';
  document.addEventListener('mouseover', (e) => {
    const target = (e.target as Element)?.closest?.('[data-tip]');
    if (!target) return;
    const text = target.getAttribute('data-tip') ?? '';
    if (!text) return;
    activeText = text;
    const el = ensure();
    el.textContent = text;
    el.classList.add('visible');
    position(el, (e as MouseEvent).clientX, (e as MouseEvent).clientY);
  });
  document.addEventListener('mousemove', (e) => {
    if (!tip || !tip.classList.contains('visible')) return;
    const target = (e.target as Element)?.closest?.('[data-tip]');
    if (!target || target.getAttribute('data-tip') !== activeText) {
      tip.classList.remove('visible');
      return;
    }
    position(tip, (e as MouseEvent).clientX, (e as MouseEvent).clientY);
  });
  document.addEventListener('mouseout', (e) => {
    const target = (e.target as Element)?.closest?.('[data-tip]');
    if (target && tip) tip.classList.remove('visible');
  });
  // A touch anywhere clears a tooltip left behind by a hover-capable stylus or a hybrid device.
  document.addEventListener('pointerdown', (e) => {
    if ((e as PointerEvent).pointerType === 'touch' && tip) tip.classList.remove('visible');
  });
}
